import {ThunkAction} from 'redux-thunk';
import {Action} from 'redux';
import {IStore} from 'redux/interface';
import {
  addReferrals,
  cmServiceAddToFavorites,
  cmServiceRemoveFromFavorites,
  getCmCountyFilters,
  getCmFilters,
  getCmService,
  getCmServices,
  getCmServicesOrg,
  getReferrals,
  removeReferrals,
  sendReferrals
} from 'redux/pageAsyncActions';
import {
  clearErrorAction,
  finishLoadingAction,
  startLoadingAction
} from 'redux/status/actions';
import {errorHandler} from 'redux/errorHandler';
import {
  checkFavoriteAction,
  checkReferralsAction,
  cmCountyFiltersLoadedAction,
  cmFiltersLoadedAction,
  cmServiceLoadedAction,
  cmServicesLoadedAction,
  cmServicesOrgLoadedAction,
  referralsLoadedAction,
  setSendReferralsStatus,
  uncheckFavoriteAction,
  uncheckReferralsAction
} from './actions';

import {ToastKey} from 'components/gritx-toast/ToastKeyEnum';
import {
  getCmCountyFiltersApi,
  getCmFiltersApi,
  getServiceApi,
  getServicesApi,
  addFavoriteApi,
  removeFavoriteApi,
  getReferralsApi,
  addReferralsApi,
  removeReferralsApi,
  shareReferralsApi
} from '../api/cmService';
import {ICmFilter, ISendReferrals} from './interfaces';

export const loadCmFiltersAction = (): ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {
    auth: {auth0Client},
    translation: {locale}
  } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = getCmFilters;

  if (isAuthenticated) {
    dispatch(startLoadingAction(actionKey));

    try {
      const {data} = await getCmFiltersApi(locale);

      dispatch(clearErrorAction(actionKey));
      dispatch(finishLoadingAction(actionKey));
      dispatch(cmFiltersLoadedAction(data));
    } catch (e) {
      console.log('getCmFilters', e.message);
      dispatch(errorHandler({
        toastKey: ToastKey.GetCmFilters,
        actionKey,
        error: e,
        currentAction: loadCmFiltersAction()
      }));
    }
  }
});

export const loadCmCountyFiltersAction = (regionIds: string): ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {
    auth: {auth0Client}
  } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = getCmCountyFilters;

  if (isAuthenticated) {
    dispatch(startLoadingAction(actionKey));

    try {
      const {data} = await getCmCountyFiltersApi(regionIds);

      dispatch(clearErrorAction(actionKey));
      dispatch(finishLoadingAction(actionKey));
      dispatch(cmCountyFiltersLoadedAction(data));
    } catch (e) {
      console.log('getCmFilters', e.message);
      dispatch(errorHandler({
        toastKey: ToastKey.GetCmCountyFilters,
        actionKey,
        error: e,
        currentAction: loadCmCountyFiltersAction(regionIds)
      }));
    }
  }
});

export const loadCmServicesAction = (filters: {
  filter: ICmFilter[],
  offset: number,
  limit: number
}): ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {
    auth: {auth0Client},
    translation: {locale}
  } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = getCmServices;

  if (isAuthenticated) {
    dispatch(startLoadingAction(actionKey));

    try {
      const {data} = await getServicesApi(filters, locale);

      dispatch(clearErrorAction(actionKey));
      dispatch(finishLoadingAction(actionKey));
      dispatch(cmServicesLoadedAction(data));
    } catch (e) {
      console.log('getCmServices', e.message);
      dispatch(errorHandler({
        toastKey: ToastKey.GetCmServices,
        actionKey,
        error: e,
        currentAction: loadCmServicesAction(filters)
      }));
    }
  }
});

export const loadReferralsAction = (): ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {
    translation: {locale},
    auth: {auth0Client}
  } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = getReferrals;

  if (isAuthenticated) {
    dispatch(startLoadingAction(actionKey));
    try {
      const {data} = await getReferralsApi(locale);

      dispatch(clearErrorAction(actionKey));
      dispatch(finishLoadingAction(actionKey));
      dispatch(referralsLoadedAction(data));
    } catch (e) {
      console.log('loadReferralsAction', e);
      dispatch(errorHandler({
        toastKey: ToastKey.LoadReferrelsAction,
        actionKey,
        error: e,
        currentAction: loadReferralsAction()
      }));
    }
  }
});

export const loadCmServiceAction = (srvId: number, turnOffCounter: boolean): ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {
    auth: {auth0Client},
    translation: {locale}
  } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = getCmService;

  if (isAuthenticated) {
    dispatch(startLoadingAction(actionKey));

    try {
      const {data} = await getServiceApi(srvId, locale, turnOffCounter);

      dispatch(clearErrorAction(actionKey));
      dispatch(finishLoadingAction(actionKey));
      dispatch(cmServiceLoadedAction(data));
    } catch (e) {
      console.log('getCmServices', e.message);
      dispatch(errorHandler({
        toastKey: ToastKey.GetCmService,
        actionKey,
        error: e,
        currentAction: loadCmServiceAction(srvId, turnOffCounter)
      }));
    }
  }
});

export const addCmServiceToFavoritesAction = (srvId: number): ThunkAction<Promise<boolean>, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = cmServiceAddToFavorites;

  if (isAuthenticated) {
    try {
      await addFavoriteApi(srvId);
      dispatch(checkFavoriteAction(srvId));

      return true;
    } catch (e) {
      console.log('addToFavoritesAction', e);
      dispatch(errorHandler({
        toastKey: ToastKey.AddCmServiceFavorite,
        actionKey,
        error: e,
        currentAction: addCmServiceToFavoritesAction(srvId)
      }));
    }
  }

  return false;
});

export const removeCmServiceFromFavoritesAction = (srvId: number): ThunkAction<Promise<boolean>, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = cmServiceRemoveFromFavorites;

  if (isAuthenticated) {
    try {
      await removeFavoriteApi(srvId);

      dispatch(uncheckFavoriteAction(srvId));

      return true;
    } catch (e) {
      console.log('removeFromFavoritesAction', e);
      dispatch(errorHandler({
        toastKey: ToastKey.RemoveCmServiceFavorite,
        actionKey,
        error: e,
        currentAction: removeCmServiceFromFavoritesAction(srvId)
      }));
    }
  }

  return false;
});

export const addReferralsAction = (srvId: number): ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {
    translation: {locale},
    auth: {auth0Client}
  } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = addReferrals;

  if (isAuthenticated) {
    try {
      await addReferralsApi({
        srvId,
        language: locale,
        comments: ''
      });
      dispatch(checkReferralsAction(srvId));
    } catch (e) {
      console.log('addReferralsAction', e);
      dispatch(errorHandler({
        toastKey: ToastKey.AddReferral,
        actionKey,
        error: e,
        currentAction: addReferralsAction(srvId)
      }));
    }
  }
});

export const removeReferralsAction = (srvIds: number[], isAll?: boolean): ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = removeReferrals;

  if (isAuthenticated) {
    try {
      await removeReferralsApi(srvIds);

      dispatch(uncheckReferralsAction(srvIds));
      if (isAll) {
        dispatch(loadReferralsAction());
      }
    } catch (e) {
      console.log('removeReferralsAction', e);
      dispatch(errorHandler({
        toastKey: ToastKey.RemoveReferral,
        actionKey,
        error: e,
        currentAction: removeReferralsAction(srvIds)
      }));
    }
  }
});

export const sendReferralsAction = (referrals: ISendReferrals): ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {
    auth: {auth0Client}
  } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = sendReferrals;

  if (isAuthenticated) {
    dispatch(startLoadingAction(actionKey));
    dispatch(clearErrorAction(actionKey));
    try {
      await shareReferralsApi(referrals);
      dispatch(finishLoadingAction(actionKey));
      dispatch(setSendReferralsStatus(true));
    } catch (e) {
      console.log('sendReferralsAction', e);
      dispatch(finishLoadingAction(actionKey));
      dispatch(setSendReferralsStatus(true));
      dispatch(errorHandler({
        toastKey: ToastKey.SendReferralsAction,
        actionKey,
        error: e
      }));
    }
  }
});

export const loadCmServicesOrgAction = (filters: {
  filter: ICmFilter[],
  offset: number,
  limit: number
}): ThunkAction<void, IStore, unknown, Action> => (async (dispatch, getState) => {
  const {
    auth: {auth0Client},
    translation: {locale}
  } = getState();
  const isAuthenticated = await auth0Client.isAuthenticated();
  const actionKey = getCmServicesOrg;

  if (isAuthenticated) {
    dispatch(startLoadingAction(actionKey));

    try {
      const {data} = await getServicesApi(filters, locale);

      dispatch(clearErrorAction(actionKey));
      dispatch(finishLoadingAction(actionKey));
      dispatch(cmServicesOrgLoadedAction(data));
    } catch (e) {
      console.log('getCmServicesOrg', e.message);
      dispatch(errorHandler({
        toastKey: ToastKey.GetCmServicesOrg,
        actionKey,
        error: e,
        currentAction: loadCmServicesOrgAction(filters)
      }));
    }
  }
});

